import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import { ForwardIcon, Separator, UnstyledButton } from '@buffer-mono/popcorn'

import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import { parseMastodonLinks } from '~publish/legacy/utils/channels/mastodon/helpers'

import {
  ReplyIcon,
  ReshareIcon,
  StarIcon,
  SaveIcon,
  DotsIcon,
  GlobeIcon,
} from '../../../../assets/mastodon'
import { useExpandablePreviewText } from '../../../Common/hooks/useExpandablePreviewText'
import useLinkPreviewEffect from '../../../Common/hooks/useLinkPreviewEffect'
import { ImagesGrid } from '../../../Common/ImagesGrid'
import { LinkPreview } from '../../../Common/LinkPreview'
import { PostWrapper } from '../../../Common/styles'
import {
  DEFAULT_AVATAR,
  getMediaProperties,
  gifAsImage,
} from '../../../Common/utils'
import { VideoPlayer } from '../../../Common/VideoPlayer'
import type { MastodonContent, MastodonProfile } from '../../../types'

import styles from './Post.module.css'

const PREVIEW_COLLAPSE_LIMIT = 500

const MastodonPost = ({
  content,
  profile,
  isReply,
}: {
  content: MastodonContent
  profile: MastodonProfile
  isReply?: boolean
}): JSX.Element => {
  const { avatar, displayName, handle, formattedUsername } = profile
  const { text, images, gif, video, link: linkFromPost } = content
  const hasContentWarning = !!content.spoilerText
  const [showContent, setShowContent] = useState(true)
  const [avatarSrc, setAvatarSrc] = useState(avatar || DEFAULT_AVATAR)

  useEffect(() => {
    setAvatarSrc(avatar || DEFAULT_AVATAR)
  }, [avatar])

  useEffect(() => {
    setShowContent(!hasContentWarning)
  }, [hasContentWarning])

  const [linkPreview] = useLinkPreviewEffect({
    linkFromPost,
    text,
    checkValidUrl: true,
  })

  const { hasImages, hasGif, hasVideo, hasLink } = getMediaProperties(
    content,
    linkPreview,
  )
  const { textToDisplay, isTruncated, isExpanded, expand } =
    useExpandablePreviewText({
      channel: 'mastodon',
      text,
      customCharLimit: PREVIEW_COLLAPSE_LIMIT,
    })
  const allImages = images?.length ? images : gif ? [gifAsImage(gif)] : []

  return (
    <PostWrapper
      className={styles.postWrapper}
      onClick={(): void => {
        setShowContent(true)
      }}
    >
      {isReply && (
        <>
          <Separator decorative={true} className={styles.separator} />
          <div className={styles.replyTo}>
            <ForwardIcon className={styles.shareArrow} />
            Replied to{' '}
            <span translate="no">{displayName || formattedUsername}</span>
          </div>
        </>
      )}
      <div className={styles.header}>
        <img
          className={styles.avatar}
          data-testid="avatar"
          src={avatarSrc}
          alt="user avatar"
          onError={(): void => {
            if (avatarSrc !== DEFAULT_AVATAR) setAvatarSrc(DEFAULT_AVATAR)
          }}
        />
        <div className={styles.usernameWrapper} translate="no">
          <div className={styles.userInfo}>
            <span className={styles.username}>
              {displayName || formattedUsername}
            </span>
          </div>
          <span className={styles.formattedUsername}>{handle}</span>
        </div>
        <div className={styles.timeWrapper}>
          <GlobeIcon size={12} />
          <span className={styles.timestamp}>1m</span>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.content}>
          {hasContentWarning && !showContent && (
            <>
              <div className={styles.contentWarning} translate="no">
                {content.spoilerText}
              </div>
              <div className={styles.showMore}>Tap anywhere to reveal</div>
            </>
          )}
          {showContent && (
            <>
              <LinkifiedText
                links={parseMastodonLinks(textToDisplay)}
                size="mini"
                whitespace="pre-wrap"
                newTab
              >
                {textToDisplay}
              </LinkifiedText>
              {isTruncated && !isExpanded && (
                <UnstyledButton
                  onClick={expand}
                  className={styles.seeMoreButton}
                >
                  ... see more
                </UnstyledButton>
              )}
            </>
          )}
        </div>
      </div>
      {(hasImages || hasGif) && (
        <ImagesGrid
          images={allImages}
          isRowFormat={false}
          maxHeight="200px"
          shouldShowGifTag
          withBlur={hasContentWarning && !showContent}
        />
      )}
      {hasVideo && video && (
        <VideoPlayer
          video={video}
          withBlur={hasContentWarning && !showContent}
        />
      )}
      {hasLink && linkPreview && (
        <LinkPreview
          className={styles.linkPreview}
          link={linkPreview}
          titleOnTop
          imageCover
          showEmptyThumbnail
          clickable={false}
        />
      )}
      <div className={styles.footer}>
        <ReplyIcon size={20} />
        <ReshareIcon size={20} />
        <StarIcon size={20} />
        <SaveIcon size={20} />
        <DotsIcon size={20} />
      </div>
    </PostWrapper>
  )
}

export default MastodonPost
